import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { InputPhone } from '@/components/ControlledInput';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { useGetIndicationMapAddressByPostalCode, usePutIndicationMapMutation } from '@/services/queries/location';
import { IndicationMap } from '@/model';
import { PutIndicationMapRequest } from '@/services/request/location';
import { ImageUpload } from '@/components/ImageUpload';

export interface IndicationMapEditModalProps {
  isOpen: boolean;
  item: IndicationMap;
  onClose: () => void;
  onDone?: () => void;
}

export const IndicationMapEditModal = ({ isOpen, onClose, item, onDone }: IndicationMapEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutIndicationMapMutation();
  const { mutate: fetchAddress } = useGetIndicationMapAddressByPostalCode();

  const {
    handleSubmit,
    control,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team?.uuid,
      title: item.title,
      description: item.description,
      address: {
        postal_code: item.address?.postal_code,
        line1: item.address?.line1,
        line2: item.address?.line2,
        line3: item.address?.line3,
        neighborhood: item.address?.neighborhood,
        city: item.address?.city,
        state: item.address?.state,
        country_code: item.address?.country_code,
      },
      latitude: item.latitude,
      longitude: item.longitude,
      phone_number: item.phone_number,
      email: item.email,
      website: item.website,
      instagram: item.instagram,
      image: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const handlePostalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const postalCode = e.target.value?.replace(/\D/g, '');
      if (postalCode.length === 8) {
        fetchAddress(postalCode, {
          onSuccess: data => {
            if (data) {
              setValue('address.line1', data.line1 || '');
              setValue('address.neighborhood', data.neighborhood || '');
              setValue('address.city', data.city || '');
              setValue('address.state', data.state || '');
              setValue('address.country_code', data.country_code || 'BR');
              if (data.latitude) setValue('latitude', data.latitude);
              if (data.longitude) setValue('longitude', data.longitude);
            }
          },
        });
      }
    },
    [fetchAddress, setValue],
  );

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }
      const payload: PutIndicationMapRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        title: data.title,
        image: data.image,
        description: data.description ?? '',
        latitude: data.latitude ?? undefined,
        longitude: data.longitude ?? undefined,
        address: {
          postal_code: data.address?.postal_code ?? '',
          line1: data.address?.line1 ?? '',
          line2: data.address?.line2 ?? '',
          line3: data.address?.line3 ?? '',
          neighborhood: data.address?.neighborhood ?? '',
          city: data.address?.city ?? '',
          state: data.address?.state ?? '',
          country_code: data.address?.country_code ?? '',
        },
        phone_number: data.phone_number ?? '',
        email: data.email ?? '',
        website: data.website ?? '',
        instagram: data.instagram ?? '',
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_indications_map.components.location_edit_modal.location_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_indications_map.components.location_edit_modal.location_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_indications_map.components.location_edit_modal.location_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_team_label')}
              placeholder={t('pages.application_indications_map.components.location_edit_modal.location_edit_team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_title_label')}
              {...register('title')}
              helperText={t('pages.application_indications_map.components.location_edit_modal.location_edit_title_helper')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputPhone
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_phone')}
              control={control}
              name='phone_number'
              rootProps={{ isInvalid: !!errors?.phone_number, errorMessage: errors?.phone_number?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_email')}
              {...register('email')}
              rootProps={{ isInvalid: !!errors?.email, errorMessage: errors?.email?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_website')}
              {...register('website')}
              rootProps={{ isInvalid: !!errors?.website, errorMessage: errors?.website?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_instagram')}
              {...register('instagram')}
              rootProps={{ isInvalid: !!errors?.instagram, errorMessage: errors?.instagram?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_postal_code')}
              {...register('address.postal_code')}
              onChange={handlePostalCodeChange}
              rootProps={{ isInvalid: !!errors?.address?.postal_code, errorMessage: errors?.address?.postal_code?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_edit_modal.location_edit_address_line1')}
              {...register('address.line1')}
              rootProps={{ isInvalid: !!errors?.address?.line1, errorMessage: errors?.address?.line1?.message }}
            />

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_address_line2')}
                {...register('address.line2')}
                rootProps={{ isInvalid: !!errors?.address?.line2, errorMessage: errors?.address?.line2?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_address_line3')}
                {...register('address.line3')}
                rootProps={{ isInvalid: !!errors?.address?.line3, errorMessage: errors?.address?.line3?.message }}
                flex='1'
              />
            </Flex>

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_neighborhood')}
                {...register('address.neighborhood')}
                rootProps={{ isInvalid: !!errors?.address?.neighborhood, errorMessage: errors?.address?.neighborhood?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_city')}
                {...register('address.city')}
                rootProps={{ isInvalid: !!errors?.address?.city, errorMessage: errors?.address?.city?.message }}
                flex='1'
              />
            </Flex>

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_state')}
                {...register('address.state')}
                helperText={t('pages.application_indications_map.components.location_edit_modal.location_edit_state_helper')}
                maxLength={2}
                rootProps={{ isInvalid: !!errors?.address?.state, errorMessage: errors?.address?.state?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_country_code')}
                {...register('address.country_code')}
                rootProps={{ isInvalid: !!errors?.address?.country_code, errorMessage: errors?.address?.country_code?.message }}
                flex='1'
              />
            </Flex>

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                type='number'
                step='any'
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_latitude')}
                {...register('latitude')}
                rootProps={{
                  isInvalid: !!errors?.latitude,
                  errorMessage: errors?.latitude?.message,
                }}
                flex='1'
              />
              <InputField
                withoutFloating
                type='number'
                step='any'
                label={t('pages.application_indications_map.components.location_edit_modal.location_edit_longitude')}
                {...register('longitude')}
                rootProps={{
                  isInvalid: !!errors?.longitude,
                  errorMessage: errors?.longitude?.message,
                }}
                flex='1'
              />
            </Flex>

            <ImageUpload
              imageURL={item?.image_url || ''}
              onFileChange={file => setValue('image', file)}
              error={errors?.image?.message?.toString()}
            />
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='space-between' gap='kilo'>
          <Flex w='100%' justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_indications_map.components.location_edit_modal.location_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_indications_map.components.location_edit_modal.location_edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
